import { Injectable } from '@angular/core';
import { EditStyleModel, FormControlBase, FormControlCustomOptions, ITFormControlType } from '../models';
import {
	AppVariables,
	ColumnModel,
	DateTimeService,
	fileBooleanMappings,
	fileDataTypesMapUI,
	FormActionTypeEnum
} from '@shared';
import { CrossTableValidationsBuilderService, ValidationRulesBuilderService } from '../validations';
import { TranslateService } from '@ngx-translate/core';
import { IAsyncValidationsBuildResult, IValidationsBuildResult } from '../validations/models';
import {
	FormControlTextbox,
	FormControlDropdown,
	FormControlDatePicker,
	FormControlCheckBox,
	FormControlTextarea,
	FormControlNumeric,
	FormControlSignature,
	FormControlGps
} from '../controls';
import { FormControlTypeProviderService } from './form-control-type-provider';
import { FormControlTypeEnum } from '../models/form-control-type.enum';
import { QueryParamsProvider } from '@domain/routing';
import { TableTypeProvider } from '../../business-layer/services/table-type.provider';
import { FormControlFormula } from '../controls/form-control-formula';
import { ColumnInformationService } from '../validations/services/column-information.service';

@Injectable()
export class FormControlFactoryService {
	public constructor(
		private translateService: TranslateService,
		private formControlTypeProviderService: FormControlTypeProviderService,
		private crossTableValidator: CrossTableValidationsBuilderService,
		private tableTypeProvider: TableTypeProvider,
		private queryParamsProvider: QueryParamsProvider,
		private dateTimeService: DateTimeService,
		private columnInformationService: ColumnInformationService
	) {}

	/* eslint-disable @typescript-eslint/no-unused-vars */
	public getFormControl<T>(
		columnModel: ColumnModel,
		primaryKeys: string[],
		columnValue?: any,
		options?: FormControlCustomOptions
	): FormControlBase<ITFormControlType> {
		const formActionType = this.queryParamsProvider.formActionType();
		const validationBuilder = new ValidationRulesBuilderService(
			this.translateService,
			this.columnInformationService
		);
		validationBuilder.setPrimaryKeys(primaryKeys);
		const buildResultSync: IValidationsBuildResult = validationBuilder.setColumnObject(columnModel).build();
		const buildResultAsync: IAsyncValidationsBuildResult = this.crossTableValidator.build(
			this.tableTypeProvider.isChildTable(),
			columnModel,
			primaryKeys
		);
		const combinedValidationMessages: Record<string, string>[] = buildResultSync.messages.concat(
			buildResultAsync.messages
		);
		const formControllType = this.formControlTypeProviderService.getFormControlType(columnModel);
		switch (formControllType) {
			case FormControlTypeEnum.Gps:
				return new FormControlGps({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value:
						columnValue !== undefined && columnValue !== null
							? columnValue
							: formActionType !== FormActionTypeEnum.edit &&
							    formActionType !== FormActionTypeEnum.newFrom
							  ? columnModel.default_value
							  : undefined,
					width: columnModel.width,
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable) || !!columnModel.calculation_formula,
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order
				});
			case FormControlTypeEnum.Signature:
				return new FormControlSignature({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value: this.handleFormDefaultValue(columnValue, formActionType, columnModel),
					width: columnModel.width,
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable) || !!columnModel.calculation_formula,
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages
				});
			case FormControlTypeEnum.Textbox:
				return new FormControlTextbox({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value: this.handleFormDefaultValue(columnValue, formActionType, columnModel),
					width: columnModel.width,
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable) || !!columnModel.calculation_formula,
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages
				});
			case FormControlTypeEnum.Textarea:
				return new FormControlTextarea({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value: this.handleFormDefaultValue(columnValue, formActionType, columnModel),
					width: columnModel.width,
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable) || !!columnModel.calculation_formula,
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages
				});
			case FormControlTypeEnum.Numeric:
				return new FormControlNumeric({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value:
						columnValue !== undefined && columnValue !== null
							? columnValue
							: formActionType !== FormActionTypeEnum.edit &&
							    formActionType !== FormActionTypeEnum.newFrom
							  ? columnModel.default_value
							  : undefined,
					width: columnModel.width,
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable) || !!columnModel.calculation_formula,
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages
				});
			case FormControlTypeEnum.Dropdown:
				return new FormControlDropdown({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value: this.handleFormDefaultValue(columnValue, formActionType, columnModel),
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable) || !!columnModel.calculation_formula,
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages,
					refTableName: columnModel.code_table_name,
					refTableColumnDescription: columnModel.code_display_column_name,
					refTableColumnValue: columnModel.code_data_column_name
				});
			case FormControlTypeEnum.DatePicker:
			case FormControlTypeEnum.TimePicker:
				return new FormControlDatePicker({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value: this.getDateTimePickerValue(columnModel, columnValue, formActionType, formControllType),
					width: columnModel.width,
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable),
					type:
						formControllType === FormControlTypeEnum.DatePicker
							? fileDataTypesMapUI[columnModel.data_type]
							: columnModel.edit_style.toLowerCase() === EditStyleModel.timeAmPm
							  ? fileDataTypesMapUI.TIME12
							  : fileDataTypesMapUI.TIME24,
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages
				});
			case FormControlTypeEnum.CheckBox:
				return new FormControlCheckBox({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value:
						columnValue !== undefined && columnValue !== null
							? columnValue
							: fileBooleanMappings[columnModel.default_value],
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable),
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages
				});
			case FormControlTypeEnum.Formula: {
				return new FormControlFormula({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value: this.handleFormDefaultValue(columnValue, formActionType, columnModel),
					width: columnModel.width,
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled: true,
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages,
					formulaToCalculate: columnModel.calculation_formula ? columnModel.calculation_formula : null,
					decimalPlaces: columnModel.decimal_places ? columnModel.decimal_places : null
				});
			}
			default:
				return new FormControlTextbox({
					key: columnModel.column_name,
					label: columnModel.column_label,
					value: this.handleFormDefaultValue(columnValue, formActionType, columnModel),
					width: columnModel.width,
					required: this.isColumnRequired(columnModel.is_column_nullable),
					disabled:
						options?.disabled !== undefined
							? Boolean(options.disabled)
							: this.isColumnDisabled(columnModel.is_editable),
					type: fileDataTypesMapUI[columnModel.data_type],
					order: columnModel.display_order,
					validators: buildResultSync.validations,
					asyncValidators: buildResultAsync.validations,
					validationMessages: combinedValidationMessages
				});
		}
	}

	private isColumnRequired(columnNullableValue: string): boolean {
		return !fileBooleanMappings[columnNullableValue];
	}

	private isColumnDisabled(columnEditableValue: string): boolean {
		return !fileBooleanMappings[columnEditableValue];
	}

	private handleFormDefaultValue(
		columnValue: any,
		formActionType: FormActionTypeEnum,
		columnModel: ColumnModel
	): string {
		if (columnValue !== undefined && columnValue !== null) {
			return columnValue;
		}

		return formActionType !== FormActionTypeEnum.edit ? columnModel.default_value : undefined;
	}

	private getDateTimePickerValue(
		columnModel: ColumnModel,
		columnValue: any,
		formActionType: FormActionTypeEnum,
		formControllType: FormControlTypeEnum
	): string {
		if (columnValue !== undefined && columnValue !== null) {
			return this.dateTimeService.fromFusionTimeFieldsFormat(columnValue);
		}

		return formActionType !== FormActionTypeEnum.edit
			? this.processDefaultDateTimeValue(columnModel, formControllType)
			: undefined;
	}

	private processDefaultDateTimeValue(columnModel: ColumnModel, formControllType: FormControlTypeEnum): string {
		if (columnModel.default_value !== AppVariables.DATE_COLUMN_DEFAULT_VALUE) {
			if (formControllType === FormControlTypeEnum.TimePicker) {
				return this.dateTimeService.fromFusionTimeFieldsFormat(columnModel.default_value);
			}

			return columnModel.default_value;
		}

		return this.dateTimeService.getSystemDateTime();
	}
}
